import type { ThemeTokens } from "@klarity/themes";
import { useId } from "react";
import { TextInput } from "../../../components/ui/Field";
import { Select } from "../../../components/ui/Select";
import type { FontInfo } from "../../../lib/api/models";
import { fontFamilies } from "./token-fields";

type FontRef = ThemeTokens["fonts"]["body"];

export function FontPicker({
  label,
  value,
  fonts,
  onChange,
  onUpload,
  error,
  readOnly = false,
}: {
  label: string;
  value: FontRef;
  fonts: FontInfo[];
  onChange: (next: FontRef) => void;
  onUpload: (file: File) => void;
  error?: string;
  readOnly?: boolean;
}) {
  const id = useId();
  const families = fontFamilies(fonts);
  const chosen = families.find((f) => f.family === value.family);
  return (
    <fieldset aria-label={label} className="grid min-w-0 grid-cols-1 gap-2 rounded-lg border border-line p-3">
      <legend className="px-1 text-sm font-medium text-ink-2">{label}</legend>
      <div role="radiogroup" aria-label={`${label} source`} className="flex gap-3 text-sm">
        {(["system", "upload"] as const).map((src) => (
          <label key={src} className="flex items-center gap-1.5">
            <input
              type="radio"
              name={`${id}-source`}
              disabled={readOnly}
              checked={value.source === src}
              onChange={() =>
                onChange(
                  src === "system"
                    ? { family: value.family, source: "system" }
                    : {
                        family: families[0]?.family ?? value.family,
                        source: "upload",
                        files: families[0]?.files ?? [],
                      },
                )
              }
            />
            {src === "system" ? "System" : "Uploaded"}
          </label>
        ))}
      </div>
      {value.source === "system" ? (
        <TextInput
          aria-label={`${label} family`}
          readOnly={readOnly}
          list={`${id}-system`}
          value={value.family}
          invalid={Boolean(error)}
          onChange={(e) => onChange({ family: e.target.value, source: "system" })}
        />
      ) : (
        <div className="flex items-center gap-2">
          <Select
            aria-label="Uploaded family"
            disabled={readOnly}
            value={chosen?.family ?? ""}
            onChange={(family) => {
              const fam = families.find((f) => f.family === family);
              if (fam) onChange({ family: fam.family, source: "upload", files: fam.files });
            }}
            options={[{ value: "", label: "Choose…" }, ...families.map((f) => ({ value: f.family, label: f.family }))]}
          />
          {chosen ? (
            <span className="text-xs text-ink-3">
              {chosen.files.length === 1 ? "1 file" : `${chosen.files.length} files`}
            </span>
          ) : null}
        </div>
      )}
      <datalist id={`${id}-system`}>
        <option value="Arial" />
        <option value="Courier New" />
        <option value="Carlito" />
        <option value="DejaVu Sans" />
      </datalist>
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      {readOnly ? null : (
        <label className="text-xs text-ink-2">
          Upload a font file
          <input
            type="file"
            accept=".ttf,.otf,.woff2"
            className="ml-2 text-xs"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onUpload(file);
              e.target.value = "";
            }}
          />
        </label>
      )}
    </fieldset>
  );
}
